// Ρυθμίσεις «Προκαταβολές στο ταμείο» (στις ρυθμίσεις Σάλας ΚΑΙ Take Away του Flow): ο διακόπτης του
// καταστήματος — ένας για τραπέζια, κρατήσεις και Take Away, πύλη σε backend ΚΑΙ UI. Από 14/10/2026 ζει στο
// κατάστημα (PUT /api/stores/:storeId/deposits/settings), όχι στις ρυθμίσεις κρατήσεων: ανοίγει και σε
// κατάστημα χωρίς σύστημα κρατήσεων. Το Ψηφιακό Τέλος της αποζημίωσης δεν ρυθμίζεται (πάντα μέσα στο ποσό,
// χρήστης 14/10/2026). Στην παραγωγή ο server αρνείται το άνοιγμα ώσπου να ολοκληρωθεί η λειτουργία
// (409 counter_deposits_not_ready) και ο μη διαχειριστής παίρνει 403: δείχνουμε το μήνυμά του.

import React, { useEffect, useState } from 'react';
import { Banknote, Loader2 } from 'lucide-react';
import { Note } from '../dineIn/floorEditor/ui';
import { DepositApiError, depositsApi } from './api';
import { useDT } from './ui';
import { invalidateDepositsConfig, loadDepositsConfig } from './useDepositsConfig';
import type { DepositsConfig } from './types';

export default function CounterDepositsSettings({ storeId }: { storeId: number }) {
  const dt = useDT();
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [config, setConfig] = useState<DepositsConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    // Φρέσκο από τον server (όχι από τη μνήμη των 5′): εδώ ο διαχειριστής αποφασίζει.
    loadDepositsConfig(storeId, true).then(c => {
      if (!alive) return;
      setConfig(c);
      setState(c ? 'ready' : 'failed');
    });
    return () => { alive = false; };
  }, [storeId]);

  const toggle = async () => {
    if (!config || saving) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const next = await depositsApi.saveSettings(storeId, { enabled: !config.enabled });
      setConfig({ ...next, enabled: next.enabled === true, ready: next.ready !== false, ecr: next.ecr === true });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      // Το μήνυμα του server (403 permission_denied, 409 counter_deposits_not_ready) αυτούσιο.
      const code = e instanceof DepositApiError ? e.code : null;
      const byCode = code ? dt(`deposits:code.${code}`, { defaultValue: '' }) : '';
      setError((e instanceof Error && e.message) || byCode || dt('deposits:err.generic'));
      // Ό,τι ισχύει στον server, όχι ό,τι πατήθηκε.
      loadDepositsConfig(storeId, true).then(c => { if (c) setConfig(c); });
    } finally {
      setSaving(false);
      // Οι άλλες οθόνες (Take Away, «Ταμείο», κρατήσεις) ξαναδιαβάζουν τον διακόπτη στο επόμενο άνοιγμα.
      invalidateDepositsConfig(storeId);
    }
  };

  if (state === 'loading') {
    return <div className="flex items-center gap-2 p-4 text-t3"><Loader2 className="h-5 w-5 animate-spin" />{dt('deposits:settings.loading')}</div>;
  }
  if (state === 'failed' || !config) return <Note tone="warn">{dt('deposits:settings.loadFailed')}</Note>;

  const enabled = config.enabled === true;

  return (
    <div className="grid gap-3" data-testid="counter-deposits-settings">
      <div className="flex items-center justify-between gap-4 rounded-2xl border border-line bg-surface-2 p-4 transition-colors hover:border-line-strong dark:bg-surface-1">
        <div className="flex items-center gap-4">
          <div className={`rounded-xl p-3 ${enabled ? 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/40 dark:text-cyan-400' : 'bg-surface-2 text-t3'}`}>
            <Banknote className="h-6 w-6" />
          </div>
          <div>
            <h4 className="font-semibold text-t1">{dt('deposits:settings.toggleLabel')}</h4>
            <p className="text-sm text-t3">{dt('deposits:settings.toggleDesc')}</p>
          </div>
        </div>
        <button type="button" role="switch" aria-checked={enabled} aria-label={dt('deposits:settings.toggleLabel')}
          disabled={saving} onClick={toggle} data-testid="counter-deposits-toggle"
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? 'bg-cyan-600' : 'bg-surface-3'}`}>
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {config.ready === false && <Note tone="warn">{dt('deposits:settings.notReady')}</Note>}
      {!config.ecr && <Note>{dt('deposits:settings.noEcr')}</Note>}
      {error && <div data-testid="counter-deposits-error"><Note tone="error">{error}</Note></div>}
      {saved && <Note tone="ok">{dt('deposits:settings.saved')}</Note>}
    </div>
  );
}
